// SwedenSound API Client v2
// Replaces direct Supabase usage with local REST API

import { FALLBACK_PRODUCTS, FALLBACK_CATEGORIES } from '../data/catalogFallback.js'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

async function apiFetch(path, options = {}) {
  const url = `${API_BASE}${path}`
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }
  return response.json()
}

// =====================
// Catalog
// =====================

export async function fetchCatalog() {
  try {
    const data = await apiFetch('/catalog')
    return data
  } catch (err) {
    console.warn('API catalog failed, using fallback:', err.message)
    // Normalize fallback specs: convert objects to arrays
    const products = FALLBACK_PRODUCTS.map(p => ({
      ...p,
      specs: Array.isArray(p.specs) ? p.specs : Object.entries(p.specs || {}).map(([key, value]) => ({ key, value })),
      category_name: p.category,
      category_slug: FALLBACK_CATEGORIES.find(c => c.id === p.category_id)?.slug || '',
    }))
    return { categories: FALLBACK_CATEGORIES, products }
  }
}

export async function fetchProductBySlug(slug) {
  try {
    const product = await apiFetch(`/products/${slug}`)
    return product
  } catch (err) {
    console.warn(`API product ${slug} failed, using fallback:`, err.message)
    const p = FALLBACK_PRODUCTS.find(p => p.slug === slug)
    if (!p) throw new Error('Product not found')
    return {
      ...p,
      specs: Array.isArray(p.specs) ? p.specs : Object.entries(p.specs || {}).map(([key, value]) => ({ key, value })),
      category_name: p.category,
      category_slug: FALLBACK_CATEGORIES.find(c => c.id === p.category_id)?.slug || '',
    }
  }
}

const MEDIA_BASE = '/products'

const SLUG_TO_IMAGE_PATH = {
  'ss-da15s': 'SS-DA15S.png',
  'ss-da18s': 'SS-DA18S.png',
  'ss-h8pro': 'SS-H8PRO.png',
  'ss-j4012a': 'SJ4012.png',
  'ss-j4015a': 'SJ4015.png',
  'ss-r10a': 'SS-10RA.png',
  'ss-r12a': 'SS-12RA.png',
  'ss-r15a': 'SS-15RA.png',
  'ss-280a': 'ss-280a.png',
  'ss200m-uv': 'ss200m-uv.png',
  'ss-pa-21': 'SS-PA_2.1.png',
  'ss-dd1238a': 'SS-DD1238A.png',
}

export function getProductImageUrl(product) {
  // Priority 1: Local file mapping (always works)
  const filename = SLUG_TO_IMAGE_PATH[product.slug]
  if (filename) return `${MEDIA_BASE}/${filename}`
  // Priority 2: DB image field (fallback)
  if (product.image) return product.image
  // Priority 3: Generate from slug
  return `/products/${product.slug}.png`
}

export function formatPrice(priceRubles) {
  if (priceRubles == null) return ''
  return Math.round(priceRubles).toLocaleString('ru-RU') + ' ₽'
}

// =====================
// Dealer Applications
// =====================

export async function submitDealerApplication(data) {
  return apiFetch('/dealer/apply', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function fetchApplications() {
  return apiFetch('/admin/applications')
}

export async function updateApplicationStatus(id, status, notes) {
  return apiFetch(`/admin/applications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, notes }),
  })
}

// =====================
// Auth
// =====================

const ADMIN_PASSWORD = 'sweden-admin-2026'


export async function loginAdmin(email, password) {
  if ((email === 'admin@swedensound.ru' || email === 'admin@sweden-sound.ru') && password === ADMIN_PASSWORD) {
    const user = {
      id: 'admin-1',
      email: email,
      role: 'admin',
      user_metadata: { role: 'admin' },
    }
    localStorage.setItem('sweden_user', JSON.stringify(user))
    return { user, error: null }
  }
  return { user: null, error: { message: 'Invalid login credentials' } }
}

export async function logoutAdmin() {
  localStorage.removeItem('sweden_user')
  return { error: null }
}

export async function getCurrentUser() {
  const stored = localStorage.getItem('sweden_user')
  if (!stored) return null
  try {
    return JSON.parse(stored)
  } catch {
    return null
  }
}

// Alias for LoginPage compatibility
// ===== DEALER LOGIN =====
export async function loginDealer(email, password) {
  try {
    const res = await fetch(API_BASE + '/dealer/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const data = await res.json()
    if (!res.ok || data.error) {
      return { user: null, error: { message: data.error || 'Invalid login credentials' } }
    }
    const user = {
      id: data.user.id,
      email: data.user.email,
      role: 'dealer',
      dealer_status: data.user.dealer_status || 'active',
      user_metadata: {
        role: 'dealer',
        company: data.user.company_name,
        full_name: data.user.full_name,
      },
    }
    localStorage.setItem('sweden_user', JSON.stringify(user))
    return { user, error: null }
  } catch (e) {
    return { user: null, error: { message: 'Login failed: ' + e.message } }
  }
}

export async function signIn(email, password) {
  // Try admin first
  const adminResult = await loginAdmin(email, password)
  if (adminResult.user) {
    return { data: { user: adminResult.user }, error: null }
  }
  // Try dealer
  const dealerResult = await loginDealer(email, password)
  if (dealerResult.user) {
    return { data: { user: dealerResult.user }, error: null }
  }
  return { data: null, error: { message: 'Invalid login credentials' } }
}

// =====================
// Backward-compatible supabase object
// =====================

export const supabase = {
  auth: {
    signInWithPassword: async ({ email, password }) => {
      const result = await signIn(email, password)
      return result.error ? { data: null, error: result.error } : { data: result, error: null }
    },
    signOut: async () => {
      await logoutAdmin()
      return { error: null }
    },
    onAuthStateChange: () => {
      return { data: { subscription: { unsubscribe: () => {} } } }
    },
    getSession: async () => {
      const user = await getCurrentUser()
      return { data: { session: user ? { user } : null }, error: null }
    },
  },
}
